import React, { createContext, useCallback, useContext, useState, ReactNode } from "react";
import { MENU } from "../components/util/Utils";

/** Optional tab that a target screen should land on when navigated to. */
export type PendingClientTab = "info" | "appointments" | "notes" | "discount";

/** Client identity used to auto-start a chat when navigating to Mensagens. */
export interface PendingChatClient {
    email: string;
    name: string;
}

interface NavigationContextType {
    activeMenu: string;
    setActiveMenu: (menu: string) => void;

    /** When set, the Team Member screen auto-opens the editor for this id. */
    pendingTeamMemberId: string | null;
    setPendingTeamMemberId: (id: string | null) => void;

    /** When set, the Clients screen auto-opens this client. Cleared after consumption. */
    pendingClientId: string | null;
    /** Which tab the Client drawer should land on (e.g. "appointments"). */
    pendingClientTab: PendingClientTab | null;
    setPendingClient: (id: string | null, tab?: PendingClientTab | null) => void;

    /** When set, the Calendar screen auto-opens this appointment in its Drawer. */
    pendingAppointmentId: string | null;
    setPendingAppointmentId: (id: string | null) => void;

    /** When set, the Calendar opens this EVENT's drawer on mount (bell deep-link
     *  for EVENT_* notifications). Cleared on consume. */
    pendingEventId: string | null;
    setPendingEventId: (id: string | null) => void;

    /** When set, the Payments screen pre-filters to this appointment's payments
     *  (the reverse of Payments' "#id → appointment" link). Cleared on consume. */
    pendingPaymentAppointmentId: string | null;
    setPendingPaymentAppointmentId: (id: string | null) => void;

    /** When set, MessagesComponent starts a conversation with this client on mount.
     *  Cleared after consumption so it doesn't re-trigger on subsequent renders. */
    pendingChatClient: PendingChatClient | null;
    setPendingChatClient: (client: PendingChatClient | null) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

/** sessionStorage key holding the active screen for same-tab reloads. */
const ACTIVE_MENU_KEY = "gs.activeMenu";

export const NavigationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // F5 / same-tab reload keeps the CURRENT screen (sessionStorage survives a
    // reload but NOT a new tab/window), while a fresh entry into the app lands
    // on Home ("Sua IA"). Users without the AI_ASSISTANT permission are bounced
    // to their first allowed screen by HomeComponent's permission redirect.
    const [activeMenu, setActiveMenuState] = useState<string>(() => {
        try {
            const saved = sessionStorage.getItem(ACTIVE_MENU_KEY);
            if (saved && Object.values(MENU).includes(saved)) return saved;
        } catch {
            /* sessionStorage unavailable → default to Home */
        }
        return MENU.HOME;
    });
    const [pendingTeamMemberId, setPendingTeamMemberIdState] = useState<string | null>(null);
    const [pendingClientId, setPendingClientIdState] = useState<string | null>(null);
    const [pendingClientTab, setPendingClientTabState] = useState<PendingClientTab | null>(null);
    const [pendingAppointmentId, setPendingAppointmentIdState] = useState<string | null>(null);
    const [pendingEventId, setPendingEventIdState] = useState<string | null>(null);
    const [pendingPaymentAppointmentId, setPendingPaymentAppointmentIdState] = useState<string | null>(null);
    const [pendingChatClient, setPendingChatClientState] = useState<PendingChatClient | null>(null);

    const setActiveMenu = useCallback((menu: string) => {
        setActiveMenuState(menu);
        try {
            sessionStorage.setItem(ACTIVE_MENU_KEY, menu);
        } catch {
            /* best-effort — navigation still works without persistence */
        }
    }, []);

    const setPendingTeamMemberId = useCallback((id: string | null) => {
        setPendingTeamMemberIdState(id);
    }, []);

    const setPendingClient = useCallback(
        (id: string | null, tab: PendingClientTab | null = null) => {
            setPendingClientIdState(id);
            setPendingClientTabState(tab);
        },
        []
    );

    const setPendingAppointmentId = useCallback((id: string | null) => {
        setPendingAppointmentIdState(id);
    }, []);

    const setPendingEventId = useCallback((id: string | null) => {
        setPendingEventIdState(id);
    }, []);

    const setPendingPaymentAppointmentId = useCallback((id: string | null) => {
        setPendingPaymentAppointmentIdState(id);
    }, []);

    const setPendingChatClient = useCallback((client: PendingChatClient | null) => {
        setPendingChatClientState(client);
    }, []);

    return (
        <NavigationContext.Provider
            value={{
                activeMenu,
                setActiveMenu,
                pendingTeamMemberId,
                setPendingTeamMemberId,
                pendingClientId,
                pendingClientTab,
                setPendingClient,
                pendingAppointmentId,
                setPendingAppointmentId,
                pendingEventId,
                setPendingEventId,
                pendingPaymentAppointmentId,
                setPendingPaymentAppointmentId,
                pendingChatClient,
                setPendingChatClient,
            }}
        >
            {children}
        </NavigationContext.Provider>
    );
};

export const useNavigation = (): NavigationContextType => {
    const ctx = useContext(NavigationContext);
    if (!ctx) {
        throw new Error("useNavigation must be used within a NavigationProvider");
    }
    return ctx;
};
